import { CloudUpload, FileText, Loader2, X } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useCurrentCompany, useCurrentUser, useSession } from "../../services/SessionContext";

const processingSteps = ["Uploading file", "Reading rows", "Translating events", "Saving report", "Done"];

export function UploadPage() {
  const { createUpload } = useSession();
  const company = useCurrentCompany();
  const user = useCurrentUser();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [controllerName, setControllerName] = useState("");
  const [projectName, setProjectName] = useState("");
  const [controllerAddress, setControllerAddress] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const [step, setStep] = useState(0);

  const fileSize = useMemo(() => {
    if (!file) return "";
    return `${Math.max(file.size / 1024, 1).toFixed(0)} KB`;
  }, [file]);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!company || !user || !file) return;
    setError("");
    setProcessing(true);
    setStep(0);
    try {
      setStep(1);
      const csvText = await file.text();
      setStep(2);
      const upload = await createUpload({
        fileName: file.name,
        fileSizeBytes: file.size,
        csvText,
        uploadedByUserId: user.userId,
        companyId: company.companyId,
        controllerName,
        projectName,
        controllerAddress,
        latitude: latitude ? Number(latitude) : undefined,
        longitude: longitude ? Number(longitude) : undefined,
      });
      setStep(4);
      navigate(`/app/uploads/${upload.uploadId}`);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "We could not read this file as a Baseline event CSV. Please confirm you uploaded the Evnt CSV file from the controller export folder.",
      );
      setProcessing(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6 pb-20 md:pb-0">
      <div>
        <h1 className="text-2xl font-bold text-navy-950">Upload Baseline event CSV</h1>
        <p className="mt-1 text-sm text-slate-600">
          Look for a file named Evnt_{"{date}"}.csv inside the controller export folder on your USB
          drive.
        </p>
      </div>

      {processing ? (
        <Card>
          <CardHeader>
            <CardTitle className="inline-flex items-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden /> Reading your Baseline event
              file...
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3">
              {processingSteps.map((label, index) => (
                <li key={label} className="flex items-center gap-3 text-sm">
                  <span
                    className={`grid h-7 w-7 place-items-center rounded-full text-xs font-bold ${
                      index <= step ? "bg-navy-900 text-white" : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    {index + 1}
                  </span>
                  <span className={index <= step ? "text-navy-900" : "text-slate-500"}>{label}</span>
                </li>
              ))}
            </ol>
          </CardContent>
        </Card>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Event file and controller details</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-5" onSubmit={submit}>
            <div className="space-y-2">
              <Label htmlFor="csv-file">CSV file</Label>
              <label
                className="flex min-h-44 cursor-pointer flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 text-center hover:bg-slate-100"
                htmlFor="csv-file"
                onDragOver={(event) => event.preventDefault()}
                onDrop={(event) => {
                  event.preventDefault();
                  setFile(event.dataTransfer.files[0] ?? null);
                }}
              >
                <CloudUpload className="h-9 w-9 text-navy-900" aria-hidden />
                <span className="mt-3 text-sm font-semibold text-navy-900">Drag & drop CSV file here</span>
                <span className="text-sm text-slate-500">or click to browse</span>
              </label>
              <Input
                id="csv-file"
                accept=".csv,text/csv"
                className="sr-only"
                required
                type="file"
                onChange={(event) => setFile(event.target.files?.[0] ?? null)}
              />
              {file ? (
                <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-white p-3">
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-navy-900" aria-hidden />
                    <div>
                      <p className="text-sm font-semibold text-navy-900">{file.name}</p>
                      <p className="text-xs text-slate-500">{fileSize}</p>
                    </div>
                  </div>
                  <Button aria-label="Remove file" size="icon" type="button" variant="ghost" onClick={() => setFile(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : null}
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="controller-name">Controller name</Label>
                <Input id="controller-name" value={controllerName} onChange={(event) => setControllerName(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project-name">Project name</Label>
                <Input id="project-name" value={projectName} onChange={(event) => setProjectName(event.target.value)} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="address">Controller address</Label>
                <Input id="address" value={controllerAddress} onChange={(event) => setControllerAddress(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="latitude">Latitude</Label>
                <Input id="latitude" inputMode="decimal" value={latitude} onChange={(event) => setLatitude(event.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="longitude">Longitude</Label>
                <Input id="longitude" inputMode="decimal" value={longitude} onChange={(event) => setLongitude(event.target.value)} />
              </div>
            </div>

            {error ? <p className="rounded-md bg-red-50 p-3 text-sm font-medium text-red-800">{error}</p> : null}

            <div className="flex flex-wrap gap-3">
              <Button disabled={!file} type="submit">
                Upload & Process
              </Button>
              <Button type="button" variant="secondary" onClick={() => navigate("/app/dashboard")}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
